import React, { useEffect, useState } from "react";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import Pagination from "../../../component/library/pagination";
import OrderDetailTable from "../../../component/views/admin/orderManagement/orderDetailsTable";
import OrderFilter from "../../../component/views/admin/orderManagement/orderFilter";
import ManualOrderForm from "../../../component/views/admin/orderManagement/manualOrderForm";
import { orderApiService } from "../../../service/admin/ordersApiService";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./orderManagement.css";

export default function OrderManagement() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const limit = 50;
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    processingOrders: 0,
    completedOrders: 0
  });
  const [showManualOrderForm, setShowManualOrderForm] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'mapped'
  const [mappedOrdersCount, setMappedOrdersCount] = useState(0);
  const [accountTypeFilter, setAccountTypeFilter] = useState(''); // '', 'btpl', or 'reseller'
  const [sourceTab, setSourceTab] = useState("importerr");

  const [pages, setPages] = useState({
    currentPage: "",
    totalPages: "",
    totalItems: "",
  });
  
  // Initialize query with URL params (e.g., status from dashboard navigation)
  const initialStatus = searchParams.get('status') || '';
  const [query, setQuery] = useState({ status: initialStatus });
  
  const message = useMessage();
  const navigate = useNavigate();

  const fetchAllOrders = () => {
    setLoading(true);
    // Build column filter params
    let columnFilterParams = '';
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        columnFilterParams += `&columnFilter[${key}]=${encodeURIComponent(JSON.stringify(values))}`;
      }
    });

    // Add fromFailedOrder filter based on active tab OR from filter dropdown
    const fromFailedOrderParam = activeTab === 'mapped' 
      ? '&fromFailedOrder=true' 
      : (query.fromFailedOrder ? `&fromFailedOrder=${query.fromFailedOrder}` : '');
    
    // Add account type filter for mapped orders tab
    const accountTypeParam = activeTab === 'mapped' && accountTypeFilter ? `&accountType=${accountTypeFilter}` : '';
    const sourceParam = `&source=${sourceTab || "importerr"}`;

    const queryParams = `?commentStatus=${
      query.commentStatus ? query.commentStatus : ""
    }&endDate=${query.endDate ? query.endDate : ""}&paymentStatus=${
      query.paymentStatus ? query.paymentStatus : ""
    }&returnStatus=${
      query.returnStatus ? query.returnStatus : ""
    }&searchFields=${query.searchFields ? query.searchFields : ""}&status=${
      query.status ? query.status : ""
    }&startDate=${query.startDate ? query.startDate : ""}&state=${
      query.state ? query.state : ""
    }&city=${query.city ? query.city : ""}&marketplace=${
      query.marketplace ? query.marketplace : ""
    }&orderType=${query.orderType ? query.orderType : ""}&orderExport=${
      query.orderExport ? query.orderExport : ""
    }&supplierOrders=${
      query.supplierOrders ? query.supplierOrders : ""
    }&ticketStatus=${query.ticket ? query.ticket : ""}&warehouseOrders=${
      query.warehouseOrders ? query.warehouseOrders : ""
    }&warehouseId=${
      query.warehouseId ? query.warehouseId : ""
    }&bankTransferConfirmOnly=${
      query.bankTransferConfirmOnly ? query.bankTransferConfirmOnly : ""
    }&page=${page}&limit=${limit}${columnFilterParams}${fromFailedOrderParam}${accountTypeParam}${sourceParam}`;
    orderApiService
      .getAllOrders(queryParams ? queryParams : "")
      .then((res) => {
        setLoading(false);
        setInitialLoading(false);
        if (res.status.code === 200) {
          setData(res.data.orders);
          setPages((prev) => ({
            ...prev,
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalOrders,
          }));
          if (res.data.currentPage !== page) {
            setPage(res.data.currentPage);
          }
          
          // Calculate stats
          const orders = res.data.orders || [];
          setStats({
            totalOrders: res.data.totalOrders || 0,
            pendingOrders: orders.filter(order => order.status === 'payment pending').length,
            processingOrders: orders.filter(order => order.status === 'processing').length,
            completedOrders: orders.filter(order => order.status === 'delivered').length
          });
        } else if (res.status.code === 404) {
          setData([]);
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        setLoading(false);
        setInitialLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  // Fetch mapped orders count for tab badge
  const fetchMappedOrdersCount = () => {
    orderApiService
      .getAllOrders('?fromFailedOrder=true&source=all&page=1&limit=1')
      .then((res) => {
        if (res.status.code === 200) {
          setMappedOrdersCount(res.data.totalOrders || 0);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchAllOrders();
  }, [query, page, limit, columnFilters, activeTab, accountTypeFilter, sourceTab]);

  useEffect(() => {
    fetchMappedOrdersCount();
  }, []);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setColumnFilters({});
    // Reset account type filter when switching tabs
    if (tab !== 'mapped') {
      setAccountTypeFilter('');
    }
  };
  
  // Handle account type filter change
  const handleAccountTypeFilterChange = (value) => {
    setAccountTypeFilter(value);
    setPage(1);
  };

  const handleQueryChange = (params = {}) => {
    setPage(1);
    setQuery(params);
  };

  const handleSourceTabChange = (source) => {
    setSourceTab(source || "importerr");
    setPage(1);
  };

  // Handle column filter changes from ExcelTable
  const handleColumnFilterChange = (columnKey, selectedValues, allFilters) => {
    setPage(1);
    setColumnFilters(allFilters);
  };

  const handleManualOrderSuccess = () => {
    fetchAllOrders();
  };

  // Fetch all orders across all pages (for Export All Data)
  const fetchAllOrdersForExport = () => {
    let columnFilterParams = '';
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        columnFilterParams += `&columnFilter[${key}]=${encodeURIComponent(JSON.stringify(values))}`;
      }
    });
    const fromFailedOrderParam = activeTab === 'mapped' 
      ? '&fromFailedOrder=true' 
      : (query.fromFailedOrder ? `&fromFailedOrder=${query.fromFailedOrder}` : '');
    const accountTypeParam = activeTab === 'mapped' && accountTypeFilter ? `&accountType=${accountTypeFilter}` : '';
    const sourceParam = `&source=${sourceTab || "importerr"}`;
    const baseParams = `commentStatus=${query.commentStatus ? query.commentStatus : ""}&endDate=${query.endDate ? query.endDate : ""}&paymentStatus=${query.paymentStatus ? query.paymentStatus : ""}&returnStatus=${query.returnStatus ? query.returnStatus : ""}&searchFields=${query.searchFields ? query.searchFields : ""}&status=${query.status ? query.status : ""}&startDate=${query.startDate ? query.startDate : ""}&state=${query.state ? query.state : ""}&city=${query.city ? query.city : ""}&marketplace=${query.marketplace ? query.marketplace : ""}&orderType=${query.orderType ? query.orderType : ""}&orderExport=${query.orderExport ? query.orderExport : ""}&supplierOrders=${query.supplierOrders ? query.supplierOrders : ""}&ticketStatus=${query.ticket ? query.ticket : ""}&warehouseOrders=${query.warehouseOrders ? query.warehouseOrders : ""}&warehouseId=${query.warehouseId ? query.warehouseId : ""}&bankTransferConfirmOnly=${query.bankTransferConfirmOnly ? query.bankTransferConfirmOnly : ""}${columnFilterParams}${fromFailedOrderParam}${accountTypeParam}${sourceParam}`;

    return orderApiService.getAllOrders(`?${baseParams}&page=1&limit=${limit}`).then((res) => {
      if (res.status.code !== 200 || !res.data?.orders) return [];
      const allOrders = [...(res.data.orders || [])];
      const totalPages = res.data.totalPages || 1;
      if (totalPages <= 1) return allOrders;

      const rest = [];
      for (let p = 2; p <= totalPages; p++) rest.push(p);
      return Promise.all(
        rest.map((p) =>
          orderApiService.getAllOrders(`?${baseParams}&page=${p}&limit=${limit}`)
        )
      ).then((results) => {
        results.forEach((r) => {
          if (r?.status?.code === 200 && Array.isArray(r.data?.orders)) {
            allOrders.push(...r.data.orders);
          }
        });
        return allOrders;
      });
    });
  };

  return (
    <div className="order-management-container">
      {/* Manual Order Form Modal */}
      <ManualOrderForm
        isOpen={showManualOrderForm}
        onClose={() => setShowManualOrderForm(false)}
        onSuccess={handleManualOrderSuccess}
      />

      {/* Stats Cards */}
      {/* <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total">
              <i className="fas fa-shopping-cart"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.totalOrders}</h3>
              <p className="stat-label">Total Orders</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon pending">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.pendingOrders}</h3>
              <p className="stat-label">Pending Payment</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon processing">
              <i className="fas fa-cog"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.processingOrders}</h3>
              <p className="stat-label">Processing</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon completed">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.completedOrders}</h3>
              <p className="stat-label">Completed</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Main Content */}
      <div className="main-content">
        {/* Filter row (includes Add Manual Order button inside) */}
        <OrderFilter
          setQuery={handleQueryChange}
          onAddManualOrder={() => setShowManualOrderForm(true)}
          initialQuery={{ status: initialStatus }}
          sourceTab={sourceTab}
          onSourceTabChange={handleSourceTabChange}
        />

        {/* Table Section */}
        <OrderDetailTable
          isLoading={initialLoading || loading} 
            data={data} 
            pages={pages} 
            limit={limit} 
            onColumnFilterChange={handleColumnFilterChange}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            mappedOrdersCount={mappedOrdersCount}
            onFetchAllOrders={fetchAllOrdersForExport}
            onRefresh={fetchAllOrders}
            accountTypeFilter={accountTypeFilter}
            onAccountTypeFilterChange={handleAccountTypeFilterChange}
            sourceTab={sourceTab}
          />

        {/* Pagination */}
        <div className="pagination-section">
          {data && data.length > 0 ? (
            <Pagination pages={pages} page={page} setPage={setPage} />
          ) : (
            <div className="no-data">
              <i className="fas fa-inbox"></i>
              <p>No orders found</p>
              <span>Try adjusting your filters or search criteria</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
